import React, { useState } from "react";
import { Layout, Menu, Row } from "antd";
import {
  TeamOutlined,
  LeftOutlined,
  RightOutlined,
  SettingOutlined,
  TransactionOutlined,
  ShakeOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import LayoutHeader from "./header";
import LayoutFooter from "./footer";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/slices/authSlice";
import { useMatchedRoute } from "../../routers";
import {
  AUDIT_TRAIL_EXPORT,
  DATA_SPONSOR_STATISTICS,
  ACCOUNT_MANAGEMENT,
  PARTNERS_LIST,
  PROVIDER_LIST,
  DASHBOARD,
  OVERVIEW,
  PACKAGES,
  PARTNERS,
  REPORT,
  STATISTICAL,
  REPORTS_ANALYTICS,
  REVENUES,
  SYSTEM,
  SYSTEM_CONFIGURATION,
  SYSTEM_CONFIG_MANAGEMENT,
  LIMIT_CHECK_ERR_CODE_PACKAGE,
  TRANSACTION_REPORTS,
  TRANSACTIONS,
  PARTNER_MANAGEMENT,
  PACKAGE_MANAGEMENT,
  TRANSACTION_OVERVIEW,
  TRANSACTION_RETRY,
} from "../../assets/constants/route_path";
import IdleSessionTimeout from "./IdleSessionTimeout";
import BreadcrumbItem from "../../components/breadcrumb";
import translate from "../../constants/translate";

const { Sider, Content } = Layout;

type MenuItem = {
  key: any;
  label: string | React.ReactNode;
  link?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
};

const buildMenuItem = ({
  key,
  label,
  link,
  icon,
  children,
  allowedfunctionids,
}: {
  label: string | React.ReactNode;
  key: string;
  link: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  allowedfunctionids?: string[];
}): MenuItem => {
  const item = {
    key,
    label,
    link,
    icon,
    children,
    allowedfunctionids,
  } as MenuItem;
  const hasChildren = children && children.length > 0;

  // Leaf item
  if (link && !hasChildren) {
    item.label = <Link to={link}>{label}</Link>;
  }

  return item;
};

const allItems: MenuItem[] = [
  buildMenuItem({
    key: "Dashboard",
    label: "Tổng quan hệ thống",
    link: DASHBOARD,
    icon: <BarChartOutlined />,
  }),
  buildMenuItem({
    key: "Transactions",
    label: "Giao dịch",
    link: TRANSACTIONS,
    icon: <TransactionOutlined />,
    children: [
      {
        key: TRANSACTIONS + TRANSACTION_OVERVIEW,
        label: <Link to={TRANSACTIONS + TRANSACTION_OVERVIEW}>Lịch sử giao dịch</Link>,
        link: TRANSACTIONS + TRANSACTION_OVERVIEW,
      },
      {
        key: TRANSACTIONS + TRANSACTION_RETRY,
        label: <Link to={TRANSACTIONS + TRANSACTION_RETRY}>Thử lại</Link>,
        link: TRANSACTIONS + TRANSACTION_RETRY,
      },
    ],
  }),
  buildMenuItem({
    key: "Packages",
    label: "Gói cước",
    link: PACKAGES,
    icon: <ShakeOutlined />,
  }),
  buildMenuItem({
    key: "Partners",
    label: 'Quản lý ví & gói cước',
    link: PARTNERS,
    icon: <TeamOutlined />,
    children: [
      {
        key: PARTNERS + PARTNER_MANAGEMENT,
        label: <Link to={PARTNERS + PARTNER_MANAGEMENT}>Ví đối tác</Link>,
        link: PARTNERS + PARTNER_MANAGEMENT,
      },
      {
        key: PARTNERS + PACKAGE_MANAGEMENT,
        label: <Link to={PARTNERS + PACKAGE_MANAGEMENT}>Gói cước đối tác</Link>,
        link: PARTNERS + PACKAGE_MANAGEMENT,
      },
    ],
  }),
  buildMenuItem({
    key: "Account Management",
    label: 'Quản lý tài khoản',
    link: ACCOUNT_MANAGEMENT,
    icon: <KeyOutlined />,
    children: [
      {
        key: ACCOUNT_MANAGEMENT + PARTNERS_LIST,
        label: <Link to={ACCOUNT_MANAGEMENT + PARTNERS_LIST}>Đối tác</Link>,
        link: ACCOUNT_MANAGEMENT + PARTNERS_LIST,
      },
      {
        key: ACCOUNT_MANAGEMENT + PROVIDER_LIST,
        label: <Link to={ACCOUNT_MANAGEMENT + PROVIDER_LIST}>Nhà cung cấp</Link>,
        link: ACCOUNT_MANAGEMENT + PROVIDER_LIST,
      },
    ],
  }),
  buildMenuItem({
    key: "Reports & Analytics",
    label: 'Báo cáo & Phân tích',
    link: REPORTS_ANALYTICS,
    icon: <BarChartOutlined />,
    children: [
      {
        key: REPORTS_ANALYTICS + TRANSACTION_REPORTS,
        label: <Link to={REPORTS_ANALYTICS + TRANSACTION_REPORTS}>Báo cáo giao dịch</Link>,
        link: REPORTS_ANALYTICS + TRANSACTION_REPORTS,
      },
      {
        key: REPORTS_ANALYTICS + AUDIT_TRAIL_EXPORT,
        label: <Link to={REPORTS_ANALYTICS + AUDIT_TRAIL_EXPORT}>Xuất nhật ký kiểm tra</Link>,
        link: REPORTS_ANALYTICS + AUDIT_TRAIL_EXPORT,
      },
      {
        key: REPORTS_ANALYTICS + DATA_SPONSOR_STATISTICS,
        label: <Link to={REPORTS_ANALYTICS + DATA_SPONSOR_STATISTICS}>Thống kê Data Sponsor</Link>,
        link: REPORTS_ANALYTICS + DATA_SPONSOR_STATISTICS,
      },
    ],
  }),
  buildMenuItem({
    key: "Revenues",
    label: 'Doanh thu',
    link: REVENUES,
    icon: <BarChartOutlined />,
    children: [
      {
        key: REVENUES + OVERVIEW,
        label: <Link to={REVENUES + OVERVIEW}>Tổng quan</Link>,
        link: REVENUES + OVERVIEW,
      },
      {
        key: REVENUES + REPORT,
        label: <Link to={REVENUES + REPORT}>Báo cáo</Link>,
        link: REVENUES + REPORT,
      },
      {
        key: REVENUES + STATISTICAL,
        label: <Link to={REVENUES + STATISTICAL}>Thống kê</Link>,
        link: REVENUES + STATISTICAL,
      },
    ],
  }),
  buildMenuItem({
    key: "System",
    label: 'Quản trị hệ thống',
    link: SYSTEM,
    icon: <SafetyCertificateOutlined />,
    children: [
      {
        key: SYSTEM + SYSTEM_CONFIGURATION,
        label: <Link to={SYSTEM + SYSTEM_CONFIGURATION}>Cấu hình hệ thống</Link>,
        link: SYSTEM + SYSTEM_CONFIGURATION,
      },
      {
        key: SYSTEM + SYSTEM_CONFIG_MANAGEMENT,
        label: <Link to={SYSTEM + SYSTEM_CONFIG_MANAGEMENT}>Quản lý cấu hình</Link>,
        link: SYSTEM + SYSTEM_CONFIG_MANAGEMENT,
      },
      {
        key: SYSTEM + LIMIT_CHECK_ERR_CODE_PACKAGE,
        label: <Link to={SYSTEM + LIMIT_CHECK_ERR_CODE_PACKAGE}>Giới hạn kiểm tra lỗi</Link>,
        link: SYSTEM + LIMIT_CHECK_ERR_CODE_PACKAGE,
      },
    ],
  }),
];

const isAuthorizedItem = (item: MenuItem, userFnIDs: string[]): boolean => {
  if (item?.allowedfunctionids && item?.allowedfunctionids?.length > 0) {
    return item?.allowedfunctionids?.some((id) => userFnIDs?.includes(id));
  }

  return true;
};

const authorizeMenuItems = (
  items: MenuItem[] = [],
  userFnIDs: string[] = [],
): MenuItem[] => {
  // Filter active items for origin array
  items = items?.filter((item) => isAuthorizedItem(item, userFnIDs));

  // Filter active items for nested items as children
  items?.forEach((item) => {
    if (item?.children && item?.children?.length > 0) {
      item.children = authorizeMenuItems(item.children, userFnIDs);
    }
  });

  return items;
};

const listAllActiveItems = (
  items: MenuItem[],
  currentPath: string | undefined,
): MenuItem[] => {
  const activeItems = items.filter(
    (item) => item.link && currentPath?.startsWith(item.link),
  );

  const childActiveItems = activeItems
    .map((item) => {
      if (item.children && item.children.length > 0) {
        return listAllActiveItems(item.children, currentPath);
      }

      return [];
    })
    .flat();

  return [...activeItems, ...childActiveItems];
};

const MainLayout = ({ children }: any) => {
  const authState = useSelector(authSelector);
  const { account } = authState;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const currentRoutePath = useMatchedRoute();
  const [collapsed, setCollapsed] = useState(false);
  console.log("account", account)
  // Avoid execute these functions multiple times
  const authorizedItems = authorizeMenuItems(allItems, account.listPermission);

  // Always expand all items
  const openedKeys = authorizedItems.map((item) => item.key);

  const activeItems = listAllActiveItems(authorizedItems, currentRoutePath);

  const activeItemLength = activeItems.length;
  const activeItemKeys = activeItems.map((item) => item.key);
  const selectedKey =
    activeItemLength === 0 ? null : activeItemKeys[activeItemLength - 1];

  const getSiderFooter = () => {
    return (
      <Row
        justify='space-between'
        align='middle'
        style={{ padding: "0 1rem " }}>
        <div style={{ fontSize: 14 }}>
          <SettingOutlined /> {translate.version} {process.env.REACT_APP_VERSION}
        </div>
        <div>
          <LeftOutlined />
        </div>
      </Row>
    );
  };
  return (
    <Layout hasSider>
      <Sider
        breakpoint='md'
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
        }}
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        trigger={!collapsed ? getSiderFooter() : <RightOutlined />}>
        {/*<Row*/}
        {/*  justify='center'*/}
        {/*  style={{ backgroundColor: "white" }}*/}
        {/*  onClick={() => navigate(getDefaultAuthenticatedRoute(account))}>*/}
        {/*  {collapsed ? (*/}
        {/*    <img*/}
        {/*      src='/images/logo-sann-group.png'*/}
        {/*      alt='SANN'*/}
        {/*      style={{*/}
        {/*        width: 75,*/}
        {/*        height: 55,*/}
        {/*        marginBottom: 8,*/}
        {/*        cursor: "pointer",*/}
        {/*      }}*/}
        {/*    />*/}
        {/*  ) : (*/}
        {/*    <img*/}
        {/*        src='/images/logo-sann-group.png'*/}
        {/*        alt='SANN'*/}
        {/*        style={{*/}
        {/*          width: 75,*/}
        {/*          height: 55,*/}
        {/*        marginTop: 10,*/}
        {/*        marginBottom: 3,*/}
        {/*        cursor: "pointer",*/}
        {/*      }}*/}
        {/*    />*/}
        {/*  )}*/}
        {/*</Row>*/}
        <Menu
          theme='dark'
          mode='inline'
          defaultOpenKeys={openedKeys}
          items={authorizedItems}
          selectedKeys={selectedKey}
          style={{ paddingBottom: 48 }}
        />
      </Sider>
      <Layout
        className='site-layout'
        style={{
          marginLeft: collapsed ? 80 : 250,
          transition: "margin-left 0.2s",
        }}>
        <LayoutHeader profile={account} collapsed={collapsed} />
        <div
          style={{
            padding: 12,
            marginTop: 64,
            paddingBottom: 0,
          }}>
          <BreadcrumbItem />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            minHeight: "calc(100vh - 100px)",
          }}>
          <Content
            style={{
              padding: 12,
              flexGrow: 1,
            }}>
            <div
              style={{
                minHeight: "100%",
                backgroundColor: "white",
              }}>
              {children}
            </div>
          </Content>
          <LayoutFooter />
        </div>
      </Layout>
      <IdleSessionTimeout account={authState.account} />
    </Layout>
  );
};

export default MainLayout;
